import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building2,
  CheckCircle2,
  Loader2,
  MailCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

type RelayRole = "owner" | "admin" | "member";

interface InviteDetails {
  id: string;
  organization_id: string;
  email: string;
  role: RelayRole;
  organization_name: string;
  expires_at?: string;
}

const ROLE_ARTICLE: Record<RelayRole, string> = {
  owner: "an",
  admin: "an",
  member: "a",
};

function mapValidateError(code?: string): string {
  switch (code) {
    case "NOT_FOUND":
    case "invite_not_found":
      return "This invitation link is no longer valid. Ask an admin to send a new one.";
    case "ALREADY_ACCEPTED":
    case "already_accepted":
      return "This invitation has already been used. Try signing in instead.";
    case "EXPIRED":
    case "invite_expired":
      return "This invitation has expired. Please request a new one.";
    default:
      return "We couldn't load this invitation. Please use the link from your email again.";
  }
}

function mapAcceptError(code?: string): string {
  switch (code) {
    case "email_mismatch":
      return "Please sign in with the email address this invite was sent to.";
    case "invite_expired":
    case "already_accepted":
      return "This invitation is no longer valid. Ask an admin to send a new one.";
    default:
      return "Could not accept the invitation. Please try again.";
  }
}

async function readFunctionErrorBody(
  error: unknown,
): Promise<{ error?: string } | null> {
  const response = (error as { context?: Response | null }).context;
  if (!response || typeof response.text !== "function") return null;

  try {
    const text = await response.text();
    if (!text) return null;

    return JSON.parse(text) as { error?: string };
  } catch {
    return null;
  }
}

// Module-scope so it has a stable identity across renders. Declaring this inside
// AcceptInvite would remount the whole card subtree on every keystroke and drop
// input focus (the mobile cursor-jump bug).
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">{children}</Card>
    </div>
  );
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setErrorMsg("This invite link is missing its token.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // validate-invite reveals only { valid, email } for a token alone; it gates
      // org/role behind a matching `email` (providedEmail). So: first learn the
      // invited email from the token, then re-call with it to unlock the details.
      const first = await supabase.functions.invoke("validate-invite", {
        body: { token },
      });
      let payload = first.data as Record<string, unknown> | null;
      let invokeError = first.error;
      if (
        !invokeError &&
        payload &&
        payload.valid &&
        payload.email &&
        !payload.organization_id
      ) {
        const second = await supabase.functions.invoke("validate-invite", {
          body: { token, email: payload.email },
        });
        if (second.error) {
          invokeError = second.error;
        } else if (second.data) {
          payload = second.data as Record<string, unknown>;
        }
      }
      if (
        invokeError ||
        !payload ||
        payload.error ||
        !payload.organization_id
      ) {
        setErrorMsg(
          mapValidateError(
            (payload?.code as string) ??
              (payload?.error as string) ??
              invokeError?.message,
          ),
        );
        return;
      }
      const role = String(payload.role);
      if (!["owner", "admin", "member"].includes(role)) {
        setErrorMsg("This invitation role isn't supported in Relay.");
        return;
      }
      setInvite({
        id: String(payload.id),
        organization_id: String(payload.organization_id),
        email: String(payload.email),
        role: role as RelayRole,
        organization_name: String(payload.organization_name ?? "your team"),
        expires_at: payload.expires_at ? String(payload.expires_at) : undefined,
      });
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Failed to load invitation.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const redirectForRole = (role: RelayRole) =>
    role === "owner" || role === "admin" ? "/settings" : "/dashboard";

  const inviteRedirectPath = token
    ? `/accept-invite?token=${encodeURIComponent(token)}`
    : "/accept-invite";
  const inviteLoginPath = invite
    ? `/login?email=${encodeURIComponent(invite.email)}&redirect=${encodeURIComponent(
        inviteRedirectPath,
      )}`
    : `/login?redirect=${encodeURIComponent(inviteRedirectPath)}`;

  const completeAccept = useCallback(async () => {
    if (!invite) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "relay-accept-invite",
        {
          body: { token },
        },
      );
      const payload = data as Record<string, unknown> | null;
      if (error || payload?.error) {
        throw new Error(
          mapAcceptError((payload?.error as string) ?? error?.message),
        );
      }
      toast.success(`You've joined ${invite.organization_name}`);
      navigate(redirectForRole(invite.role));
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not accept the invitation.",
      );
    } finally {
      setBusy(false);
    }
  }, [invite, token, navigate]);

  const handleSwitchAccount = async () => {
    setSwitchingAccount(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) throw error;
      navigate(inviteLoginPath, { replace: true });
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Could not sign out. Please try again.",
      );
    } finally {
      setSwitchingAccount(false);
    }
  };

  const handleSignUpAndAccept = async () => {
    if (!invite) return;
    if (password.length < 8) {
      toast.error("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      // Create the account PRE-CONFIRMED via the token-gated EF. The invite link was
      // emailed to them, so it already proves they own the address — no second
      // confirmation email is needed (that was the dead end). Then sign them in.
      const { data, error } = await supabase.functions.invoke(
        "relay-invite-signup",
        {
          body: { token, password },
        },
      );
      const payload =
        (data as { error?: string } | null) ??
        (error ? await readFunctionErrorBody(error) : null);
      if (error || payload?.error) {
        const code = payload?.error ?? "";
        if (code === "user_exists") {
          toast.info("You already have an account — please sign in.");
          navigate(inviteLoginPath);
          return;
        }
        throw new Error(
          code
            ? mapAcceptError(code)
            : (error?.message ?? "Could not create your account."),
        );
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password,
      });
      if (signInError) throw signInError;
      toast.success(`You've joined ${invite.organization_name}`);
      navigate(redirectForRole(invite.role));
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not create your account.",
      );
    } finally {
      setBusy(false);
    }
  };

  // --- render -------------------------------------------------------------

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorMsg || !invite) {
    return (
      <Shell>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Invitation unavailable</CardTitle>
          <CardDescription>{errorMsg}</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button className="w-full" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </CardContent>
      </Shell>
    );
  }

  // Signed in
  if (user) {
    const emailMismatch =
      user.email?.toLowerCase() !== invite.email.toLowerCase();
    return (
      <Shell>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Join {invite.organization_name}</CardTitle>
          <CardDescription>
            You've been invited to join as {ROLE_ARTICLE[invite.role]}{" "}
            <strong>{invite.role}</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {emailMismatch && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-700/20 dark:text-amber-200">
              You're signed in as <strong>{user.email}</strong>, but this invite
              was sent to <strong>{invite.email}</strong>. Switch accounts to
              accept with the invited address.
            </div>
          )}
          {emailMismatch && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy || switchingAccount}
              onClick={handleSwitchAccount}
            >
              {switchingAccount ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MailCheck className="h-4 w-4 mr-2" />
              )}
              {switchingAccount ? "Signing out…" : `Switch to ${invite.email}`}
            </Button>
          )}
          <Button
            className="w-full"
            disabled={busy || emailMismatch}
            onClick={() => completeAccept()}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Accept invitation
          </Button>
        </CardContent>
      </Shell>
    );
  }

  // Signed out → create account (email pinned to the invite)
  return (
    <Shell>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>Join {invite.organization_name}</CardTitle>
        <CardDescription>
          You've been invited to join as {ROLE_ARTICLE[invite.role]}{" "}
          <strong>{invite.role}</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">
          Creating your account as <strong>{invite.email}</strong>
        </p>
        <div className="space-y-2">
          <Label htmlFor="invite-password">Password</Label>
          <Input
            id="invite-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-confirm">Confirm password</Label>
          <Input
            id="invite-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat your password"
            autoComplete="new-password"
          />
        </div>
        <Button
          className="w-full"
          disabled={busy}
          onClick={handleSignUpAndAccept}
        >
          {busy ? "Creating account..." : "Create account & join"}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(inviteLoginPath)}
        >
          I already have an account
        </Button>
      </CardContent>
    </Shell>
  );
}
